"use client";

import Image from "next/image";
import { cn } from "@workspace/ui/lib/utils";
import { useQuizState } from "../hooks/use-quiz-state";
import { Button } from "@workspace/ui/components/button";
import { CrossIcon } from "../../ui/icons/cross-icon";
import { useCallback, useEffect, useRef } from "react";
import { COOKIE_BRIDGE_EVENTS } from "@/lib/cookie-bridge";
import { useQuizTouchpointImpressionVisible } from "../hooks/use-quiz-touchpoint-impression";

const takeQuizImage = "https://cdn.inito.com/inito_website/home/floating-banner.png";

export function FloatingQuizCard({visible, onClose}: {visible: boolean, onClose: () => void}) {
  const { handleQuizTap, ctaLabel } = useQuizState();
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  useQuizTouchpointImpressionVisible("floating_banner", visible);

  const handleClick = useCallback(() => {
    handleQuizTap("floating_banner");
  }, [handleQuizTap]);

  useEffect(() => {
    if (visible) {
      document.body.classList.add("cc-behind-app-modal");
      window.dispatchEvent(
        new window.CustomEvent(COOKIE_BRIDGE_EVENTS.APP_MODAL_OPEN)
      );
    } else {
      document.body.classList.remove("cc-behind-app-modal");
      window.dispatchEvent(new window.CustomEvent(COOKIE_BRIDGE_EVENTS.APP_MODAL_CLOSE));
    }
  }, [visible]);

  // Re-assert cc-behind-app-modal when another consumer (e.g. modal-provider)
  // removes it on modal close while the floating card is still visible.
  useEffect(() => {
    const reassertClass = () => {
      if (visibleRef.current) {
        document.body.classList.add("cc-behind-app-modal");
      }
    };
    window.addEventListener(COOKIE_BRIDGE_EVENTS.APP_MODAL_CLOSE, reassertClass);
    return () => {
      window.removeEventListener(COOKIE_BRIDGE_EVENTS.APP_MODAL_CLOSE, reassertClass);
    };
  }, []);

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-[50] px-5 pb-5 mt-[30px] sm:right-12 sm:left-auto",
        "bg-transparent",
        "transition-all duration-500 ease-in-out",
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-full opacity-0 pointer-events-none",
      )}
    >
      <div className="mx-auto max-w-[430px]">
        <div className="relative flex overflow-hidden rounded-2xl border border-white/20 bg-[rgba(35,34,33,0.5)] backdrop-blur-lg shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <button
            type="button"
            id="floating-quiz-card-close"
            data-testid="floating-quiz-card-close"
            aria-label="Dismiss quiz card"
            onClick={onClose}
            className="absolute right-2 top-2.5 max-[390px]:top-4 z-10 flex size-6 items-center justify-center rounded-full bg-black/[0.18] backdrop-blur-[4px] cursor-pointer"
          >
            <CrossIcon className="size-4 aspect-square" color="white" />
          </button>

          <div className="flex flex-1 flex-col gap-4 md:gap-5 p-2.5 md:py-4 md:justify-between">
            <p
              id="floating-quiz-card-title"
              data-testid="floating-quiz-card-title"
              className="title-16 md:title-20 text-white"
            >
              Still unsure how Inito will help in your fertility journey?
            </p>
            <Button
              size="sm"
              id="floating-quiz-card-cta"
              data-testid="floating-quiz-card-cta"
              onClick={handleClick}
              className="self-start rounded-[10px] bg-white px-[30px] py-2 title-14 md:title-16 text-primary cursor-pointer transition-colors hover:bg-white/90"
            >
              {ctaLabel}
            </Button>
          </div>

          <div className="relative w-[120px] md:w-[150px] aspect-square shrink-0 self-center rounded-lg overflow-hidden m-1">
            <Image
              src={takeQuizImage}
              alt=""
              fill
              className="object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
